import { Box, ButtonBase, InputBase } from '@mui/material'
import {
  AttachFile as AttachIcon,
  MicNoneOutlined as MicIcon,
} from '@mui/icons-material'
import { useRef, type ChangeEvent, type KeyboardEvent } from 'react'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { fieldInputSx } from '@/apps/latest/shared/styles/inlineInput'
import { RADIUS } from '@/shared/styles/radii'
import { ICON_SIZE, UI_FONT_SIZE } from '@/shared/styles/typography'

import { StopSquare } from './CounselLine'

export type MicState = 'idle' | 'recording' | 'busy'

const PLACEHOLDER: Record<MicState, string> = {
  idle: 'nagi に話す',
  recording: '録音中。もう一度押すと送信',
  busy: '書き起こし中',
}

// 28px の ghost の四角 (写真と mic)。主役は欄なので primary の色を持たせない。
// タッチのときだけ当たり判定を 44px にする
const squareGhostSx = {
  position: 'relative',
  flex: 'none',
  width: 28,
  height: 28,
  borderRadius: `${RADIUS.md}px`,
  color: colorTokens.fg.muted,
  transition: 'background-color 120ms',
  '&:hover': { backgroundColor: colorTokens.overlay.hover },
  '&.Mui-focusVisible': { boxShadow: colorTokens.focus.ringNeutral },
  '&.Mui-disabled': { color: colorTokens.fg.faint },
  '& svg': { fontSize: ICON_SIZE.md },
  '@media (pointer: coarse)': {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: -8,
    },
  },
} as const

// Enter で送る・Shift + Enter で改行。変換中の Enter (確定) は送らない
export const isSendKey = (e: KeyboardEvent) =>
  e.key === 'Enter' &&
  !e.shiftKey &&
  !e.nativeEvent.isComposing &&
  e.keyCode !== 229

interface Props {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onPhoto: (file: File) => void
  micState: MicState
  onMic: () => void
  maxLength: number
  // 返事を作成中は送れない (1 往復ずつ)
  isBusy: boolean
}

/**
 * 最下部の 1 行 (design-book 04-apps/kakeru.md がんの相談)。[写真] [欄] [mic]。
 * 欄は 16px で 1〜6 行に伸び、Enter で送る。送るボタンは置かない — 欄が何をするかを placeholder が言う。
 * 写真は選んだらすぐ送る (欄の文字も一緒に)。mic は 押す → 話す → 押す で、書き起こした文がそのまま送られる。
 */
export const CounselComposer = ({
  value,
  onChange,
  onSend,
  onPhoto,
  micState,
  onMic,
  maxLength,
  isBusy,
}: Props) => {
  const photoRef = useRef<HTMLInputElement>(null)

  const handlePhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) onPhoto(file)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isSendKey(e)) return
    e.preventDefault()
    if (!isBusy) onSend()
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
      <ButtonBase
        aria-label="写真を送る"
        title="写真を送る"
        disabled={isBusy || micState !== 'idle'}
        onClick={() => photoRef.current?.click()}
        sx={{ ...squareGhostSx, mb: '4px' }}
      >
        <AttachIcon sx={{ transform: 'rotate(45deg)' }} />
      </ButtonBase>
      <input
        ref={photoRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={handlePhoto}
      />
      <InputBase
        multiline
        maxRows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={PLACEHOLDER[micState]}
        readOnly={micState !== 'idle'}
        inputProps={{
          'aria-label': 'nagi に話す',
          maxLength,
          enterKeyHint: 'send',
        }}
        sx={{
          ...fieldInputSx,
          '& textarea::placeholder': {
            color: colorTokens.fg.faint,
            opacity: 1,
          },
        }}
      />
      <ButtonBase
        aria-label={
          micState === 'recording' ? '録音を終えて送る' : '話して送る'
        }
        title={micState === 'recording' ? '録音を終えて送る' : '話して送る'}
        disabled={micState === 'busy' || (isBusy && micState === 'idle')}
        onClick={onMic}
        sx={{
          ...squareGhostSx,
          mb: '4px',
          ...(micState === 'recording' && {
            backgroundColor: colorTokens.state.infoBg,
            color: colorTokens.state.successFg,
            '&:hover': { backgroundColor: colorTokens.state.infoBg },
          }),
        }}
      >
        {micState === 'recording' ? <StopSquare size={10} /> : <MicIcon />}
      </ButtonBase>
    </Box>
  )
}

// 欄の下の 1 行 (失敗の理由)。平常時は描かない
export const ComposerNote = ({ text }: { text: string | null }) =>
  text ? (
    <Box
      sx={{
        mt: 1,
        fontSize: UI_FONT_SIZE.tag,
        lineHeight: 1.4,
        color: colorTokens.fg.faint,
      }}
    >
      {text}
    </Box>
  ) : null
